import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Edit3, Plus, Search, Mail, MessageCircle, Calendar, UserCheck, UserX,
  Users as UsersIcon, ShieldCheck, AlertCircle, X,
} from "lucide-react";
import { Switch } from "@material-tailwind/react";
import AddUserRole from "./AddUserRole";
import axiosInstance from "../../services/api/axios";
import { useUser } from "../../context/UserContext";
import { IconButton } from "../../components/Buttons";

const STAFF_PERSONAS = new Set(["STAFF", "BV_ASSOCIATE"]);

const personaTheme = {
  STAFF: {
    label: "Staff",
    chip: "bg-flamingo-50 text-flamingo-700 border-flamingo-200",
    gradient: "from-flamingo-400 to-flamingo-600",
  },
  BV_ASSOCIATE: {
    label: "BV User",
    chip: "bg-slate-100 text-slate-700 border-slate-200",
    gradient: "from-slate-500 to-slate-700",
  },
};

const StatCard = ({ icon: Icon, label, value, accent }) => {
  // Brand palette: flamingo (primary) + slate/grey shades only. Status
  // semantics (active/expired/error) use rose; nothing else uses green/yellow.
  const tones = {
    flamingo: { ring: "ring-flamingo-100", icon: "bg-flamingo-500 text-white" },
    slate: { ring: "ring-slate-100", icon: "bg-slate-700 text-white" },
    "slate-soft": { ring: "ring-slate-100", icon: "bg-slate-500 text-white" },
    gray: { ring: "ring-gray-100", icon: "bg-gray-600 text-white" },
  };
  const t = tones[accent] || tones.flamingo;
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 px-4 py-3.5 flex items-center gap-3 ring-1 ${t.ring}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.icon}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-gray-900 leading-tight">{value}</p>
      </div>
    </div>
  );
};

const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "U";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const UserCard = ({ user, onEdit, onToggle }) => {
  const theme = personaTheme[user.persona] || personaTheme.STAFF;
  return (
    <div
      className={`group relative bg-white rounded-2xl border ${
        user.active ? "border-gray-200" : "border-gray-200 opacity-75"
      } shadow-sm hover:shadow-md hover:border-flamingo-200 transition-all duration-200 overflow-hidden`}
    >
      {/* Top stripe */}
      <div className={`h-1.5 bg-gradient-to-r ${theme.gradient}`} />

      <div className="p-5">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div
            className={`w-12 h-12 rounded-xl bg-gradient-to-br ${theme.gradient} text-white flex items-center justify-center font-bold text-base shadow-sm shrink-0`}
          >
            {initialsOf(user.name || user.username)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                {user.name || "Unnamed"}
              </h3>
              {!user.active && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-rose-100 text-rose-700">
                  Inactive
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 truncate">@{user.username}</p>
            <span
              className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${theme.chip}`}
            >
              <ShieldCheck size={11} />
              {theme.label}
            </span>
          </div>
        </div>

        <div className="mt-4 space-y-1.5 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Mail size={13} className="text-gray-400 shrink-0" />
            <span className="truncate" title={user.email}>
              {user.email || <span className="text-gray-400 italic">no email</span>}
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <MessageCircle size={13} className="text-gray-400 shrink-0" />
            <span className="truncate">
              {user.mobileNoWa || <span className="text-gray-400 italic">no whatsapp</span>}
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <Calendar size={12} className="text-gray-400 shrink-0" />
            <span>Joined {formatDate(user.createdDateRaw)}</span>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              checked={user.active}
              color="orange"
              onChange={() => onToggle(user)}
            />
            <span className="text-xs font-medium text-gray-600">
              {user.active ? "Active" : "Disabled"}
            </span>
          </div>
          <button
            onClick={() => onEdit(user)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <Edit3 size={12} />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, user, isActivate = false }) => {
  if (!isOpen || !user) return null;
  const Icon = isActivate ? UserCheck : UserX;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md relative overflow-hidden">
        <div
          className={`px-6 py-5 flex items-center gap-3 ${
            isActivate ? "bg-flamingo-50" : "bg-rose-50"
          }`}
        >
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isActivate ? "bg-flamingo-500" : "bg-rose-500"
            } text-white`}
          >
            <Icon size={18} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {isActivate ? "Activate user?" : "Deactivate user?"}
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              {isActivate
                ? "This user will be able to log in again."
                : "This user will not be able to log in until reactivated."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-700">
            User: <span className="font-semibold">{user.username}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">{user.name}</p>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm(user.id, user.username);
              onClose();
            }}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${
              isActivate
                ? "bg-flamingo-500 hover:bg-flamingo-600"
                : "bg-rose-500 hover:bg-rose-600"
            }`}
          >
            {isActivate ? "Activate" : "Deactivate"}
          </button>
        </div>
      </div>
    </div>
  );
};

const RolesDashboard = () => {
  const [rowData, setRowData] = useState([]);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [userToActivate, setUserToActivate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [personaFilter, setPersonaFilter] = useState("ALL");
  const user = useUser();

  const showToast = (message, type = "success") => toast[type === "warn" ? "warn" : type](message);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const loginIdentifier = user.userInfo.loginIdentifierDetails.customer.customerId;
        const response = await axiosInstance.get("/login", { params: { loginIdentifier } });
        const transformed = response
          .filter((u) => STAFF_PERSONAS.has(u.persona))
          .map((u) => ({
            id: u.loginId,
            username: u.username,
            name: u.name,
            mobileNoWa: u.mobileNoWa,
            email: u.email,
            persona: u.persona,
            createdDateRaw: u.createdDate,
            createdDate: new Date(u.createdDate).toLocaleString(),
            active: u.active,
          }));
        setRowData(transformed);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, refreshData]);

  const handleDeactivate = async (userId, username) => {
    try {
      await axiosInstance.put(`/login?loginId=${userId}`, {
        loginIdentifier: user.userInfo.loginIdentifierDetails.customer.customerId,
        active: false,
      });
      showToast("User deactivated");
      setRowData((prev) =>
        prev.map((u) => (u.username === username ? { ...u, active: false } : u))
      );
    } catch (error) {
      showToast(error?.response?.data?.detail || "Failed to deactivate user", "error");
    }
  };

  const handleActivate = async (userId, username) => {
    try {
      await axiosInstance.put(`/login?loginId=${userId}`, {
        loginIdentifier: user.userInfo.loginIdentifierDetails.customer.customerId,
        active: true,
      });
      showToast("User activated");
      setRowData((prev) =>
        prev.map((u) => (u.username === username ? { ...u, active: true } : u))
      );
    } catch (error) {
      showToast(error?.response?.data?.detail || "Failed to activate user", "error");
    }
  };

  const handleToggle = (u) => {
    if (u.active) {
      setUserToDeactivate(u);
      setIsDeactivateModalOpen(true);
    } else {
      setUserToActivate(u);
      setIsActivateModalOpen(true);
    }
  };

  const handleDataChange = () => setRefreshData((p) => !p);

  // Filtering
  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rowData.filter((u) => {
      if (personaFilter !== "ALL" && u.persona !== personaFilter) return false;
      if (!q) return true;
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.username || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.mobileNoWa || "").toLowerCase().includes(q)
      );
    });
  }, [rowData, searchQuery, personaFilter]);

  const stats = useMemo(() => {
    const total = rowData.length;
    const active = rowData.filter((u) => u.active).length;
    const staff = rowData.filter((u) => u.persona === "STAFF").length;
    const bv = rowData.filter((u) => u.persona === "BV_ASSOCIATE").length;
    return { total, active, staff, bv };
  }, [rowData]);

  return (
    <div className="space-y-5">
      {/* Header strip — dark slate gradient, matches FormModal and the
          Onboarding/Settings section headers app-wide. */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 to-slate-600 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Users</h2>
          <p className="text-xs text-slate-300 mt-0.5">
            Manage staff and BV User accounts. Permissions are assigned in Role Based Access.
          </p>
        </div>
        <IconButton
          left
          icon={<Plus size={16} className="mr-2" />}
          onClick={() => {
            setSelectedUser(null);
            setIsAddUserModalOpen(true);
          }}
        >
          Add User
        </IconButton>
      </div>

      {/* Stats — flamingo (brand) + slate/grey shades only */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={UsersIcon} label="Total Users" value={stats.total} accent="slate" />
        <StatCard icon={UserCheck} label="Active" value={stats.active} accent="flamingo" />
        <StatCard icon={ShieldCheck} label="Staff" value={stats.staff} accent="slate-soft" />
        <StatCard icon={ShieldCheck} label="BV Users" value={stats.bv} accent="gray" />
      </div>

      {/* Search + persona filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, username, email or whatsapp…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400"
          />
        </div>
        <div className="inline-flex p-1 bg-gray-100 rounded-xl">
          {[
            { id: "ALL", label: "All" },
            { id: "STAFF", label: "Staff" },
            { id: "BV_ASSOCIATE", label: "BV User" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setPersonaFilter(opt.id)}
              className={`px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                personaFilter === opt.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-200 p-5 animate-pulse"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                  <div className="h-2 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-2 bg-gray-100 rounded" />
                <div className="h-2 bg-gray-100 rounded w-5/6" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 px-6 py-12 text-center">
          <div className="w-14 h-14 mx-auto rounded-xl bg-gray-100 flex items-center justify-center">
            <AlertCircle size={24} className="text-gray-400" />
          </div>
          <p className="mt-3 text-sm font-semibold text-gray-700">No users found</p>
          <p className="text-xs text-gray-500 mt-1">
            {searchQuery || personaFilter !== "ALL"
              ? "Try clearing your filters."
              : "Click \"Add User\" above to create the first one."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredUsers.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              onEdit={(uu) => {
                setSelectedUser(uu);
                setIsAddUserModalOpen(true);
              }}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}

      <AddUserRole
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        selectedUser={selectedUser}
        onUserSaved={handleDataChange}
      />
      <ConfirmationModal
        isOpen={isDeactivateModalOpen}
        onClose={() => setIsDeactivateModalOpen(false)}
        onConfirm={handleDeactivate}
        user={userToDeactivate}
        isActivate={false}
      />
      <ConfirmationModal
        isOpen={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        onConfirm={handleActivate}
        user={userToActivate}
        isActivate={true}
      />
    </div>
  );
};

export default RolesDashboard;
